import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection, doc, getDoc, setDoc, addDoc, updateDoc, deleteDoc,
  getDocs, onSnapshot, serverTimestamp, Timestamp, writeBatch,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import {
  getWeekStart, getWeekDays, toDateStr, formatDayLabel,
  formatTime, formatWeekRange, getShiftColor, ROLE_COLORS, ZONE_COLORS, FLOOR_ZONE_COLORS,
} from './scheduleUtils';
import CreateShiftModal from './CreateShiftModal';
import ShiftMarketplace from './ShiftMarketplace';

const fncs = getFunctions();
const notifySchedulePublish = httpsCallable(fncs, 'notifySchedulePublish');

// ── Grid shift bar (proportional horizontal timeline bar for main grid) ───────
// Must be defined before ScheduleView (const, not hoisted).

const GridShiftBar = ({
  shift, colorMode, canEdit, onEdit, onDelete, onDragStart,
  addMode, onAddClick, openM, daySpan,
}) => {
  const [hovered, setHovered] = useState(false);
  const hex      = barHex(shift, colorMode);
  const startM   = tsToMins(shift.startTime);
  const endM     = tsToMins(shift.endTime);
  const leftPct  = Math.max(0, ((startM - openM) / daySpan) * 100);
  const widthPct = Math.min(100 - leftPct, Math.max(4, ((endM - startM) / daySpan) * 100));

  const handleClick = (e) => {
    e.stopPropagation();
    if (addMode) { onAddClick?.(); return; }
    if (canEdit) onEdit();
  };

  return (
    <div className="relative h-7 w-full shrink-0">
      <div
        draggable={canEdit && !addMode}
        onDragStart={e => !addMode && onDragStart(e, shift)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={handleClick}
        title={`${formatTime(shift.startTime)}–${formatTime(shift.endTime)}${shift.role ? ` · ${shift.role}` : ''}${shift.zone ? ` · ${shift.zone}` : ''}${shift.floorZone != null ? ` · Zone ${shift.floorZone}` : ''}${shift.notes ? ` · ${shift.notes}` : ''}`}
        className={`absolute inset-y-0.5 rounded flex items-center px-1.5 cursor-pointer overflow-hidden transition-all ${
          addMode ? 'ring-2 ring-yellow-300 ring-offset-1 hover:ring-yellow-400' : hovered ? 'opacity-80' : ''
        }`}
        style={{
          left:            `${leftPct}%`,
          width:           `${widthPct}%`,
          backgroundColor: `${hex}28`,
          border:          `1px solid ${hex}55`,
          borderLeftWidth: '3px',
          borderLeftColor: hex,
        }}
      >
        {widthPct > 8 && (
          <span className="text-[10px] font-semibold truncate leading-none" style={{ color: hex }}>
            {shift.role}
          </span>
        )}
      </div>
    </div>
  );
};

// ── Shift card (stacked bubble used in the main grid) ────────────────────────
// Shows role, time, and zone badges all in one card — no truncation.

const ShiftCard = ({ shift, colorMode, canEdit, onEdit, onDelete, onDragStart, addMode, onAddClick }) => {
  const [hovered, setHovered] = useState(false);
  const colorCls = getShiftColor(shift, colorMode);

  const handleClick = (e) => {
    e.stopPropagation();
    if (addMode) { onAddClick?.(); return; }
    if (canEdit) onEdit();
  };

  const floorZoneBg = shift.floorZone != null
    ? FLOOR_ZONE_COLORS[(Number(shift.floorZone) - 1) % FLOOR_ZONE_COLORS.length]
    : null;

  return (
    <div
      draggable={canEdit && !addMode}
      onDragStart={e => !addMode && onDragStart(e, shift)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      className={`relative rounded-lg border px-2 py-1.5 text-sm cursor-pointer select-none transition-all ${colorCls} ${
        addMode
          ? 'ring-2 ring-yellow-300 ring-offset-1 hover:ring-yellow-400'
          : hovered ? 'shadow-md' : 'shadow-sm'
      }`}
    >
      {/* Role — wraps, never truncates */}
      <p className="font-semibold leading-tight break-words">{shift.role || 'Shift'}</p>

      {/* Time range */}
      <p className="text-[10px] opacity-75 leading-tight">
        {formatTime(shift.startTime)}–{formatTime(shift.endTime)}
      </p>

      {/* Zone badges — always visible, never truncated */}
      {(shift.zone || shift.floorZone != null) && (
        <div className="flex flex-wrap gap-1 mt-1">
          {shift.floorZone != null && (
            <span
              className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm text-white leading-none"
              style={{ backgroundColor: floorZoneBg }}
            >
              Z{shift.floorZone}
            </span>
          )}
          {shift.zone && (
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm leading-none text-white ${
              shift.zone === 'FOH' ? 'bg-sky-700' :
              shift.zone === 'BOH' ? 'bg-stone-700' :
              shift.zone === 'Bar' ? 'bg-violet-700' : 'bg-neutral-700'
            }`}>
              {shift.zone}
            </span>
          )}
        </div>
      )}

      {shift.notes && (
        <p className="text-[10px] opacity-60 leading-tight break-words italic mt-0.5">{shift.notes}</p>
      )}

      {/* Delete button — hidden in addMode */}
      {canEdit && hovered && !addMode && (
        <button
          onClick={e => { e.stopPropagation(); onDelete(); }}
          className="absolute top-0.5 right-0.5 w-4 h-4 flex items-center justify-center rounded-full bg-black bg-opacity-20 text-white text-[10px] hover:bg-opacity-40 transition-colors"
        >
          ✕
        </button>
      )}
    </div>
  );
};

/**
 * ScheduleView — manager/owner schedule builder.
 * Week grid: employees (rows) × days (columns).
 * Supports drag-and-drop, shift creation/editing/deletion,
 * bulk actions, publish, and color-by-role/zone.
 */
const ScheduleView = ({ user, restaurantId }) => {
  const [weekStart, setWeekStart]     = useState(() => getWeekStart());
  const [schedule, setSchedule]       = useState(null);       // schedule doc
  const [shifts, setShifts]           = useState([]);
  const [employees, setEmployees]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [creating, setCreating]       = useState(false);      // creating schedule doc
  const [publishing, setPublishing]   = useState(false);
  const [colorMode, setColorMode]     = useState('role');     // 'role' | 'zone'
  const [subTab, setSubTab]           = useState('grid');     // 'grid' | 'marketplace' | 'changelog'
  const [draggedShift, setDraggedShift]   = useState(null);
  const [dragOverCell, setDragOverCell]   = useState(null);   // { employeeId, date }
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalCell, setCreateModalCell] = useState({ date: null, employeeId: null });
  const [editShift, setEditShift]     = useState(null);
  const [changelog, setChangelog]     = useState([]);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [undoStack, setUndoStack] = useState([]);  // [{type, ...}] newest last
  const [undoing, setUndoing]     = useState(false);
  const [restaurantHours, setRestaurantHours] = useState(null);
  const [tableZones, setTableZones]           = useState([]);   // sorted unique floor zone numbers
  const [addMode, setAddMode]     = useState(false); // floating FAB "add shift" mode
  const [notifyMap, setNotifyMap] = useState({});   // { [empId]: bool } — default true per employee

  const weekDays    = getWeekDays(weekStart);
  const scheduleId  = toDateStr(weekStart); // "YYYY-MM-DD" as doc ID
  const byName           = `${user.firstName} ${user.lastName}`;
  // canEdit: can build and manage schedules (draft or any)
  const canEdit          = user.role === 'owner' || user.role === 'manager';
  // canEditPublished: can make changes after a schedule is published
  const canEditPublished = user.role === 'owner' || !!user.permissions?.editSchedule;
  // activeCanEdit: the effective edit gate used throughout the grid
  const activeCanEdit    = canEdit && (schedule?.status !== 'published' || canEditPublished);

  // Pre-compute each day's timeline window (restaurant hours + shift extents)
  const dayWindows = useMemo(() => {
    const days = getWeekDays(weekStart);
    return days.map(day => {
      const dow      = day.getDay();
      const dateStr  = toDateStr(day);
      const dayHours = (restaurantHours || []).find(h => h.dayOfWeek === dow);
      const closed   = dayHours?.isClosed || false;
      let openM  = (dayHours && !closed) ? timeStrToMins(dayHours.openTime)  : 11 * 60;
      let closeM = (dayHours && !closed) ? timeStrToMins(dayHours.closeTime) : 22 * 60;
      const dayShifts = shifts.filter(s => s.date === dateStr);
      if (dayShifts.length > 0) {
        openM  = Math.min(openM,  ...dayShifts.map(s => tsToMins(s.startTime)));
        closeM = Math.max(closeM, ...dayShifts.map(s => tsToMins(s.endTime)));
      }
      return { openM, closeM, daySpan: Math.max(closeM - openM, 1) };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, shifts, restaurantHours]);

  // ── Load employees ──────────────────────────────────────────────────────────

  useEffect(() => {
    getDocs(collection(db, 'restaurants_data', restaurantId, 'employees'))
      .then(snap => setEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(err => console.error('Failed to load employees:', err));
  }, [restaurantId]);

  // ── Load restaurant hours (for Coverage timeline) ────────────────────────────

  useEffect(() => {
    getDoc(doc(db, 'restaurants_data', restaurantId))
      .then(snap => {
        if (!snap.exists()) return;
        const data = snap.data();
        setRestaurantHours(data?.hours?.regularHours || null);
        // Extract unique non-zero floor zones from the tables array
        const tables = data?.tables || [];
        const zones = [...new Set(
          tables.map(t => t.tableZone).filter(z => z != null && z !== 0 && z !== '')
        )].sort((a, b) => Number(a) - Number(b));
        setTableZones(zones);
      })
      .catch(() => {});
  }, [restaurantId]);

  // ── Add-mode: exit on Escape, week change, or sub-tab change ─────────────────

  useEffect(() => {
    if (!addMode) return;
    const onKey = (e) => { if (e.key === 'Escape') setAddMode(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [addMode]);

  useEffect(() => { setAddMode(false); }, [weekStart, subTab]);

  // ── Load schedule doc ───────────────────────────────────────────────────────

  useEffect(() => {
    setLoading(true);
    setShifts([]);
    setSchedule(null);
    setChangelog([]);
    setUndoStack([]);

    const loadSchedule = async () => {
      try {
        const snap = await getDoc(
          doc(db, 'restaurants_data', restaurantId, 'schedules', scheduleId)
        );
        if (snap.exists()) {
          setSchedule({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.error('Failed to load schedule:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSchedule();
  }, [restaurantId, scheduleId]);

  // ── Listen to shifts + changelog once schedule exists ───────────────────────
  // Kept separate from schedule loading so that writes (duplicate, create shift)
  // are immediately reflected — the listener fires for every subcollection change
  // regardless of how the schedule doc was created or when state was set.

  useEffect(() => {
    if (!schedule) return;

    const unsubShifts = onSnapshot(
      collection(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'shifts'),
      s => setShifts(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    const unsubChangelog = onSnapshot(
      collection(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'changelog'),
      s => {
        const entries = s.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.at?.seconds || 0) - (a.at?.seconds || 0));
        setChangelog(entries);
      }
    );

    return () => { unsubShifts(); unsubChangelog(); };
  // schedule?.id is stable within a week — only changes when the week changes or
  // the schedule is first created, which are the exact moments we need to re-subscribe.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule?.id, restaurantId, scheduleId]);

  // ── Load pending marketplace count ──────────────────────────────────────────

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'restaurants_data', restaurantId, 'shiftSwapRequests'),
      snap => setPendingCount(snap.docs.filter(d => d.data().status === 'pending').length)
    );
    return unsub;
  }, [restaurantId]);

  // ── Week navigation ─────────────────────────────────────────────────────────

  const goWeek = (offset) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + offset * 7);
    setWeekStart(next);
  };

  // ── Create schedule for this week ───────────────────────────────────────────

  const createSchedule = async () => {
    setCreating(true);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    try {
      await setDoc(
        doc(db, 'restaurants_data', restaurantId, 'schedules', scheduleId),
        {
          weekStart:     Timestamp.fromDate(weekStart),
          weekEnd:       Timestamp.fromDate(weekEnd),
          status:        'draft',
          createdAt:     serverTimestamp(),
          createdBy:     user.accountId,
          createdByName: byName,
          publishedAt:   null,
          publishedBy:   null,
          publishedByName: null,
        }
      );
      const snap = await getDoc(
        doc(db, 'restaurants_data', restaurantId, 'schedules', scheduleId)
      );
      setSchedule({ id: snap.id, ...snap.data() });
    } catch (err) {
      console.error('Failed to create schedule:', err);
    } finally {
      setCreating(false);
    }
  };

  // ── Publish ─────────────────────────────────────────────────────────────────

  const publishSchedule = async () => {
    setPublishing(true);
    try {
      await updateDoc(
        doc(db, 'restaurants_data', restaurantId, 'schedules', scheduleId),
        { status: 'published', publishedAt: serverTimestamp(), publishedBy: user.accountId, publishedByName: byName }
      );
      await addDoc(
        collection(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'changelog'),
        { action: 'schedule_published', shiftId: null, by: user.accountId, byName, at: serverTimestamp(), before: { status: 'draft' }, after: { status: 'published' } }
      );
      setSchedule(prev => ({ ...prev, status: 'published', publishedByName: byName }));
      setConfirmPublish(false);
      setUndoStack([]);

      // Send all queued notifications (non-fatal — a failed email doesn't undo the publish)
      notifySchedulePublish({ restaurantId, scheduleId }).catch(err =>
        console.warn('Schedule notifications failed (non-fatal):', err)
      );
    } catch (err) {
      console.error('Publish failed:', err);
    } finally {
      setPublishing(false);
    }
  };

  // ── Undo helpers ─────────────────────────────────────────────────────────────

  // Capture the fields needed to fully recreate or revert a shift
  const shiftSnapshot = (shift) => ({
    id:              shift.id,
    employeeId:      shift.employeeId      || null,
    employeeName:    shift.employeeName    || '',
    role:            shift.role            || '',
    startTime:       shift.startTime,
    endTime:         shift.endTime,
    date:            shift.date            || '',
    zone:            shift.zone            || null,
    floorZone:       shift.floorZone      ?? null,
    notes:           shift.notes          || '',
    isOpenShift:     !!shift.isOpenShift,
    notifyOnPublish: !!shift.notifyOnPublish,
    status:          shift.status         || 'scheduled',
  });

  const executeUndo = async (op) => {
    const shiftsCol = collection(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'shifts');

    if (op.type === 'delete') {
      await deleteDoc(doc(shiftsCol, op.shiftId));

    } else if (op.type === 'restore') {
      const { id, ...data } = op.shiftData;
      await setDoc(doc(shiftsCol, id), { ...data, updatedAt: serverTimestamp() });

    } else if (op.type === 'revert') {
      await updateDoc(doc(shiftsCol, op.shiftId), { ...op.previousData, updatedAt: serverTimestamp() });

    } else if (op.type === 'restore_batch') {
      const batch = writeBatch(db);
      op.shifts.forEach(({ id, ...data }) =>
        batch.set(doc(shiftsCol, id), { ...data, updatedAt: serverTimestamp() })
      );
      await batch.commit();

    } else if (op.type === 'delete_batch') {
      const batch = writeBatch(db);
      op.shiftIds.forEach(id => batch.delete(doc(shiftsCol, id)));
      await batch.commit();
    }
  };

  const undoLast = async () => {
    if (!undoStack.length || undoing) return;
    setUndoing(true);
    const op = undoStack[undoStack.length - 1];
    try {
      await executeUndo(op);
      setUndoStack(prev => prev.slice(0, -1));
    } catch (err) {
      console.error('Undo failed:', err);
    } finally {
      setUndoing(false);
    }
  };

  const undoAll = async () => {
    if (!undoStack.length || undoing) return;
    setUndoing(true);
    try {
      for (let i = undoStack.length - 1; i >= 0; i--) {
        await executeUndo(undoStack[i]);
      }
      setUndoStack([]);
    } catch (err) {
      console.error('Undo all failed:', err);
    } finally {
      setUndoing(false);
    }
  };

  // Called by CreateShiftModal after a successful save
  const handleShiftSaved = ({ type, shiftId }) => {
    if (type === 'shift_created') {
      setUndoStack(prev => [...prev, { type: 'delete', shiftId }]);
    } else if (type === 'shift_updated' && editShift) {
      setUndoStack(prev => [...prev, { type: 'revert', shiftId, previousData: shiftSnapshot(editShift) }]);
    }
  };

  // ── Delete shift ────────────────────────────────────────────────────────────

  const deleteShift = async (shift) => {
    const snapshot = shiftSnapshot(shift);
    try {
      await deleteDoc(
        doc(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'shifts', shift.id)
      );
      await addDoc(
        collection(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'changelog'),
        { action: 'shift_deleted', shiftId: shift.id, by: user.accountId, byName, at: serverTimestamp(), before: { employeeId: shift.employeeId, role: shift.role, date: shift.date }, after: null }
      );
      setUndoStack(prev => [...prev, { type: 'restore', shiftData: snapshot }]);
    } catch (err) {
      console.error('Delete shift failed:', err);
    }
  };

  // ── Duplicate previous week ─────────────────────────────────────────────────

  const duplicatePreviousWeek = async () => {
    const prevId = toDateStr(new Date(weekStart.getTime() - 7 * 86400000));
    try {
      const prevSnap = await getDocs(
        collection(db, 'restaurants_data', restaurantId, 'schedules', prevId, 'shifts')
      );
      if (prevSnap.empty) return alert('No shifts found in the previous week.');

      const batch    = writeBatch(db);
      const newIds   = [];
      prevSnap.docs.forEach(sd => {
        const s = sd.data();
        const offsetMs = 7 * 86400000;
        const newStart = new Date(s.startTime.toDate().getTime() + offsetMs);
        const newEnd   = new Date(s.endTime.toDate().getTime() + offsetMs);
        const newDate  = toDateStr(newStart);

        const newRef = doc(
          collection(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'shifts')
        );
        newIds.push(newRef.id);
        batch.set(newRef, {
          ...s,
          date: newDate,
          startTime: Timestamp.fromDate(newStart),
          endTime:   Timestamp.fromDate(newEnd),
          status:    'scheduled',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          updatedBy: user.accountId,
          updatedByName: byName,
        });
      });
      await batch.commit();
      setUndoStack(prev => [...prev, { type: 'delete_batch', shiftIds: newIds }]);
      await addDoc(
        collection(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'changelog'),
        { action: 'week_duplicated', shiftId: null, by: user.accountId, byName, at: serverTimestamp(), before: { source: prevId }, after: { target: scheduleId } }
      );
    } catch (err) {
      console.error('Duplicate week failed:', err);
    }
  };

  // ── Clear day ───────────────────────────────────────────────────────────────

  const clearDay = async (dateStr) => {
    const toDelete = shifts.filter(s => s.date === dateStr);
    if (!toDelete.length) return;
    const snapshots = toDelete.map(shiftSnapshot);
    const batch = writeBatch(db);
    toDelete.forEach(s => {
      batch.delete(doc(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'shifts', s.id));
    });
    await batch.commit();
    setUndoStack(prev => [...prev, { type: 'restore_batch', shifts: snapshots }]);
    await addDoc(
      collection(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'changelog'),
      { action: 'day_cleared', shiftId: null, by: user.accountId, byName, at: serverTimestamp(), before: { date: dateStr, count: toDelete.length }, after: null }
    );
  };

  // ── Drag and drop ───────────────────────────────────────────────────────────

  const handleDragStart = (e, shift) => {
    e.dataTransfer.effectAllowed = 'move';
    setDraggedShift(shift);
  };

  const handleDrop = async (targetEmployeeId, targetDate) => {
    if (!draggedShift) return;
    if (draggedShift.employeeId === targetEmployeeId && draggedShift.date === targetDate) {
      setDraggedShift(null);
      setDragOverCell(null);
      return;
    }

    const before = { employeeId: draggedShift.employeeId, date: draggedShift.date };
    const previousData = {
      employeeId:   draggedShift.employeeId   || null,
      employeeName: draggedShift.employeeName || '',
      date:         draggedShift.date,
      startTime:    draggedShift.startTime,
      endTime:      draggedShift.endTime,
      isOpenShift:  !!draggedShift.isOpenShift,
    };

    // Shift times stay the same, just move to target date
    const startOld = draggedShift.startTime.toDate();
    const endOld   = draggedShift.endTime.toDate();
    const [ty, tm, td] = targetDate.split('-').map(Number);
    const newStart = new Date(ty, tm - 1, td, startOld.getHours(), startOld.getMinutes(), 0);
    const newEnd   = new Date(ty, tm - 1, td, endOld.getHours(),   endOld.getMinutes(),   0);

    const targetEmployee = employees.find(e => e.id === targetEmployeeId);

    try {
      await updateDoc(
        doc(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'shifts', draggedShift.id),
        {
          employeeId:    targetEmployeeId || null,
          employeeName:  targetEmployee ? `${targetEmployee.firstName} ${targetEmployee.lastName}` : '',
          date:          targetDate,
          startTime:     Timestamp.fromDate(newStart),
          endTime:       Timestamp.fromDate(newEnd),
          isOpenShift:   !targetEmployeeId,
          updatedAt:     serverTimestamp(),
          updatedBy:     user.accountId,
          updatedByName: byName,
        }
      );
      await addDoc(
        collection(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'changelog'),
        { action: 'shift_updated', shiftId: draggedShift.id, by: user.accountId, byName, at: serverTimestamp(), before, after: { employeeId: targetEmployeeId, date: targetDate } }
      );
      setUndoStack(prev => [...prev, { type: 'revert', shiftId: draggedShift.id, previousData }]);
    } catch (err) {
      console.error('Drag drop update failed:', err);
    } finally {
      setDraggedShift(null);
      setDragOverCell(null);
    }
  };

  // ── Cell click → open create modal ─────────────────────────────────────────

  const openCreate = (employeeId, date) => {
    setEditShift(null);
    setCreateModalCell({ employeeId, date });
    setShowCreateModal(true);
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const getShiftsForCell = (empId, dateStr) =>
    shifts.filter(s => s.employeeId === (empId || null) && s.date === dateStr);

  const openShiftsForDate = (dateStr) =>
    shifts.filter(s => s.isOpenShift && s.date === dateStr);

  // notifyMap defaults to true; false only if explicitly set
  const getNotify = (empId) => empId ? notifyMap[empId] !== false : true;
  const toggleNotify = (empId) =>
    setNotifyMap(prev => ({ ...prev, [empId]: !getNotify(empId) }));

  // The notify value to pass to the modal — derived from the employee being scheduled
  const modalEmployeeId = editShift ? editShift.employeeId : createModalCell.employeeId;
  const modalNotify = getNotify(modalEmployeeId);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* ── Sub-tabs ── */}
      <div className="flex gap-2 overflow-x-auto border-b border-neutral-200 pb-3 scrollbar-none -mx-1 px-1">
        {[
          { id: 'grid',        label: 'Schedule' },
          { id: 'zones',       label: 'Zones' },
          { id: 'coverage',    label: 'Coverage'  },
          { id: 'marketplace', label: `Marketplace${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
          { id: 'changelog',   label: 'Activity' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`shrink-0 text-sm font-medium px-4 py-1.5 rounded-lg transition-colors ${
              subTab === t.id
                ? 'bg-neutral-900 text-white'
                : 'text-neutral-500 hover:text-neutral-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Marketplace ── */}
      {subTab === 'marketplace' && (
        <ShiftMarketplace user={user} restaurantId={restaurantId} />
      )}

      {/* ── Coverage ── */}
      {subTab === 'coverage' && (
        <>
          {/* Week controls (navigation only) */}
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-2">
              <button onClick={() => goWeek(-1)} className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors">←</button>
              <span className="text-sm font-semibold text-neutral-700 min-w-[180px] text-center">
                {formatWeekRange(weekStart)}
              </span>
              <button onClick={() => goWeek(1)} className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors">→</button>
              <button
                onClick={() => setWeekStart(getWeekStart())}
                className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg text-neutral-500 hover:bg-neutral-50 transition-colors"
              >
                This week
              </button>
            </div>
            <div className="flex text-sm border border-neutral-300 rounded-lg overflow-hidden">
              {['role', 'zone'].map(m => (
                <button
                  key={m}
                  onClick={() => setColorMode(m)}
                  className={`px-3 py-1.5 font-medium transition-colors ${
                    colorMode === m ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:bg-neutral-50'
                  }`}
                >
                  Color by {m}
                </button>
              ))}
            </div>
          </div>

          {loading && <div className="text-center py-16 text-sm text-neutral-400">Loading…</div>}

          {!loading && !schedule && (
            <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-neutral-200 rounded-xl">
              <p className="text-sm text-neutral-400">No schedule for this week.</p>
            </div>
          )}

          {!loading && schedule && (
            <DayCoverageView
              weekDays={weekDays}
              shifts={shifts}
              employees={employees}
              restaurantHours={restaurantHours}
              colorMode={colorMode}
              onEdit={(shift) => { setEditShift(shift); setShowCreateModal(true); }}
            />
          )}
        </>
      )}

      {/* ── Changelog ── */}
      {subTab === 'changelog' && (
        <ChangelogPanel changelog={changelog} />
      )}

      {/* ── Zones ── */}
      {subTab === 'zones' && (
        <>
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-2">
              <button onClick={() => goWeek(-1)} className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors">←</button>
              <span className="text-sm font-semibold text-neutral-700 min-w-[180px] text-center">
                {formatWeekRange(weekStart)}
              </span>
              <button onClick={() => goWeek(1)} className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors">→</button>
              <button
                onClick={() => setWeekStart(getWeekStart())}
                className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg text-neutral-500 hover:bg-neutral-50 transition-colors"
              >
                This week
              </button>
            </div>
          </div>

          {loading && <div className="text-center py-16 text-sm text-neutral-400">Loading…</div>}

          {!loading && !schedule && (
            <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-neutral-200 rounded-xl">
              <p className="text-sm text-neutral-400">No schedule for this week.</p>
            </div>
          )}

          {!loading && schedule && (
            <ZoneRosterView
              weekDays={weekDays}
              shifts={shifts}
              tableZones={tableZones}
              activeCanEdit={activeCanEdit}
              restaurantId={restaurantId}
              scheduleId={scheduleId}
              onEditShift={(shift) => { setEditShift(shift); setShowCreateModal(true); }}
            />
          )}
        </>
      )}

      {/* ── Grid ── */}
      {subTab === 'grid' && (
        <>
          {/* Week header */}
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-2">
              <button onClick={() => goWeek(-1)} className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors">←</button>
              <span className="text-sm font-semibold text-neutral-700 min-w-[180px] text-center">
                {formatWeekRange(weekStart)}
              </span>
              <button onClick={() => goWeek(1)} className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors">→</button>
              <button
                onClick={() => setWeekStart(getWeekStart())}
                className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg text-neutral-500 hover:bg-neutral-50 transition-colors"
              >
                This week
              </button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Color mode toggle */}
              <div className="flex text-sm border border-neutral-300 rounded-lg overflow-hidden">
                {['role', 'zone'].map(m => (
                  <button
                    key={m}
                    onClick={() => setColorMode(m)}
                    className={`px-3 py-1.5 font-medium transition-colors ${
                      colorMode === m ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:bg-neutral-50'
                    }`}
                  >
                    Color by {m}
                  </button>
                ))}
              </div>

              {schedule && canEdit && (
                <>
                  {activeCanEdit && (
                    <button
                      onClick={duplicatePreviousWeek}
                      className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg text-neutral-600 hover:bg-neutral-50 transition-colors"
                    >
                      Duplicate prev. week
                    </button>
                  )}

                  {/* Undo buttons — available whenever activeCanEdit is true */}
                  {activeCanEdit && undoStack.length > 0 && (
                    <>
                      <button
                        onClick={undoLast}
                        disabled={undoing}
                        title={`Undo last change (${undoStack.length} pending)`}
                        className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 transition-colors"
                      >
                        {undoing ? '…' : '↩ Undo'}
                      </button>
                      {undoStack.length > 1 && (
                        <button
                          onClick={undoAll}
                          disabled={undoing}
                          title={`Undo all ${undoStack.length} changes`}
                          className="px-3 py-1.5 text-sm border border-red-200 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-40 transition-colors"
                        >
                          Undo All ({undoStack.length})
                        </button>
                      )}
                    </>
                  )}

                  {schedule.status === 'draft' && (
                    <button
                      onClick={() => setConfirmPublish(true)}
                      className="px-4 py-1.5 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition-colors"
                    >
                      Publish Schedule
                    </button>
                  )}
                  {schedule.status === 'published' && (
                    activeCanEdit && undoStack.length > 0 ? (
                      <button
                        onClick={() => setConfirmPublish(true)}
                        className="px-4 py-1.5 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition-colors"
                      >
                        Republish
                      </button>
                    ) : (
                      <span className="px-3 py-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg">
                        ✓ Published{schedule.publishedByName ? ` by ${schedule.publishedByName}` : ''}
                      </span>
                    )
                  )}
                </>
              )}
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="text-center py-16 text-sm text-neutral-400">Loading schedule…</div>
          )}

          {/* No schedule yet */}
          {!loading && !schedule && (
            <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-neutral-200 rounded-xl">
              <p className="text-sm font-medium text-neutral-500 mb-1">No schedule for this week</p>
              <p className="text-sm text-neutral-400 mb-4">Create one to start adding shifts.</p>
              {canEdit && (
                <button
                  onClick={createSchedule}
                  disabled={creating}
                  className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                >
                  {creating ? 'Creating…' : 'Create Schedule for This Week'}
                </button>
              )}
            </div>
          )}

          {/* Published lock banner — shown to managers without editSchedule permission */}
          {!loading && schedule?.status === 'published' && canEdit && !canEditPublished && (
            <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              <span className="text-base">🔒</span>
              <span>Your account must be permitted access to edit published schedules.</span>
            </div>
          )}

          {/* Add-mode banner */}
          {addMode && (
            <div className="flex items-center gap-2.5 px-4 py-2.5 bg-yellow-50 border border-yellow-300 rounded-xl text-sm text-yellow-800 font-medium animate-pulse">
              <span className="text-base leading-none">✦</span>
              <span>Tap any cell or existing shift to place a new shift there. Press <kbd className="px-1 py-0.5 text-sm bg-yellow-100 border border-yellow-300 rounded font-mono">Esc</kbd> or click <strong>Add Shift</strong> again to cancel.</span>
            </div>
          )}

          {/* Grid */}
          {!loading && schedule && (
            <div className={`overflow-x-auto rounded-xl border bg-white transition-all duration-200 ${addMode ? 'border-yellow-400 ring-4 ring-yellow-200 shadow-lg shadow-yellow-100' : 'border-neutral-200'}`}>
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-neutral-50 border-b border-neutral-200">
                    <th className="text-left px-4 py-3 text-sm font-semibold text-neutral-500 w-36 sticky left-0 bg-neutral-50 z-10 border-r-2 border-neutral-300">
                      Employee
                    </th>
                    {weekDays.map((day, i) => {
                      const dateStr = toDateStr(day);
                      const isToday = dateStr === toDateStr(new Date());
                      return (
                        <th key={i} className="text-center px-2 py-3 text-sm font-semibold min-w-[150px] border-r border-neutral-200 last:border-r-0">
                          <div className={`inline-flex flex-col items-center gap-0.5 ${isToday ? 'text-blue-600' : 'text-neutral-500'}`}>
                            <span>{formatDayLabel(day)}</span>
                            {activeCanEdit && (
                              <div className="flex items-center gap-2 mt-0.5">
                                <button
                                  onClick={(e) => { e.stopPropagation(); openCreate(null, dateStr); }}
                                  title="Add shift for this day"
                                  className="w-5 h-5 flex items-center justify-center bg-neutral-700 text-white rounded-full text-xs font-bold hover:bg-neutral-500 transition-colors leading-none"
                                >
                                  +
                                </button>
                                <button
                                  onClick={() => clearDay(dateStr)}
                                  title="Clear day"
                                  className="text-neutral-300 hover:text-red-400 text-sm transition-colors"
                                >
                                  clear
                                </button>
                              </div>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {/* Open shifts row — pinned to top */}
                  <tr className="border-b-2 border-neutral-300 bg-white">
                    <td className="px-4 py-2 sticky left-0 bg-white z-10 border-r-2 border-neutral-300">
                      <p className="font-semibold text-neutral-600 text-sm">Open Shifts</p>
                    </td>
                    {weekDays.map((day, i) => {
                      const dateStr = toDateStr(day);
                      const open    = openShiftsForDate(dateStr);
                      const isOver  = dragOverCell?.employeeId === 'open' && dragOverCell?.date === dateStr;
                      return (
                        <td
                          key={i}
                          className={`px-1 py-1 align-top cursor-pointer transition-colors border-r border-neutral-200 last:border-r-0 ${isOver ? 'bg-amber-100' : ''}`}
                          onClick={() => activeCanEdit && openCreate(null, dateStr)}
                          onDragOver={e => { e.preventDefault(); setDragOverCell({ employeeId: 'open', date: dateStr }); }}
                          onDragLeave={() => setDragOverCell(null)}
                          onDrop={e => { e.preventDefault(); handleDrop(null, dateStr); }}
                        >
                          <div className="flex flex-col gap-1 min-h-[40px] p-1">
                            {open.map(shift => (
                              <ShiftCard
                                key={shift.id}
                                shift={shift}
                                colorMode={colorMode}
                                canEdit={activeCanEdit}
                                onEdit={() => { setEditShift(shift); setShowCreateModal(true); }}
                                onDelete={() => deleteShift(shift)}
                                onDragStart={handleDragStart}
                                addMode={addMode}
                                onAddClick={() => openCreate(null, dateStr)}
                              />
                            ))}
                          </div>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Employee rows */}
                  {employees.map(emp => (
                    <tr key={emp.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                      <td className="px-4 py-1 sticky left-0 bg-white z-10 border-r-2 border-neutral-300">
                        <p className="font-medium text-neutral-700 text-sm leading-tight">{emp.firstName} {emp.lastName}</p>
                        {emp.position && <p className="text-neutral-400 text-xs">{emp.position}</p>}
                        {activeCanEdit && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleNotify(emp.id); }}
                            className="flex items-center gap-1 mt-1 group"
                            title={getNotify(emp.id) ? 'Email notifications on — click to disable' : 'Email notifications off — click to enable'}
                          >
                            <span className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors shrink-0 ${getNotify(emp.id) ? 'bg-neutral-800' : 'bg-neutral-200'}`}>
                              <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${getNotify(emp.id) ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                            </span>
                            <span className="text-[10px] text-neutral-400 group-hover:text-neutral-600 transition-colors leading-none">
                              {getNotify(emp.id) ? 'Notify' : 'Muted'}
                            </span>
                          </button>
                        )}
                      </td>
                      {weekDays.map((day, i) => {
                        const dateStr  = toDateStr(day);
                        const cellShifts = getShiftsForCell(emp.id, dateStr);
                        const isOver   = dragOverCell?.employeeId === emp.id && dragOverCell?.date === dateStr;
                        return (
                          <td
                            key={i}
                            className={`px-1 py-0.5 align-top cursor-pointer transition-colors border-r border-neutral-200 last:border-r-0 ${isOver ? 'bg-blue-50' : ''}`}
                            onClick={() => activeCanEdit && openCreate(emp.id, dateStr)}
                            onDragOver={e => { e.preventDefault(); setDragOverCell({ employeeId: emp.id, date: dateStr }); }}
                            onDragLeave={() => setDragOverCell(null)}
                            onDrop={e => { e.preventDefault(); handleDrop(emp.id, dateStr); }}
                          >
                            <div className="flex flex-col gap-1 min-h-[36px] p-1">
                              {cellShifts.map(shift => (
                                <ShiftCard
                                  key={shift.id}
                                  shift={shift}
                                  colorMode={colorMode}
                                  canEdit={activeCanEdit}
                                  onEdit={() => { setEditShift(shift); setShowCreateModal(true); }}
                                  onDelete={() => deleteShift(shift)}
                                  onDragStart={handleDragStart}
                                  addMode={addMode}
                                  onAddClick={() => openCreate(emp.id, dateStr)}
                                />
                              ))}
                              {cellShifts.length === 0 && isOver && (
                                <div className="h-10 border-2 border-dashed border-blue-300 rounded-lg" />
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Color legend */}
          {schedule && (
            <div className="flex flex-wrap gap-2 pt-1">
              {Object.entries(colorMode === 'role' ? ROLE_COLORS : ZONE_COLORS).map(([label, cls]) => (
                <span key={label} className={`text-sm px-2 py-0.5 rounded border ${cls}`}>{label}</span>
              ))}
            </div>
          )}

          {/* Notification footnote */}
          {schedule && activeCanEdit && (
            <p className="text-xs text-neutral-400 pt-1">
              No emails are sent until this schedule is published. When published, all queued notifications go out together in a single email per person.
            </p>
          )}
        </>
      )}

      {/* Publish confirm */}
      {confirmPublish && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-base font-semibold text-neutral-800 mb-2">
              {schedule?.status === 'published' ? 'Republish this schedule?' : 'Publish this schedule?'}
            </h3>
            <p className="text-sm text-neutral-500 mb-5">
              {schedule?.status === 'published'
                ? 'Employees will be re-notified with the latest shift changes. This action is logged.'
                : 'Employees will be able to see their shifts. This action is logged.'}
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmPublish(false)} className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800">
                Cancel
              </button>
              <button
                onClick={publishSchedule}
                disabled={publishing}
                className="px-5 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 disabled:opacity-50 transition-colors"
              >
                {publishing ? 'Publishing…' : schedule?.status === 'published' ? 'Republish' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit modal */}
      <CreateShiftModal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); setEditShift(null); }}
        onSaved={handleShiftSaved}
        onDelete={editShift ? () => { deleteShift(editShift); setShowCreateModal(false); setEditShift(null); } : null}
        restaurantId={restaurantId}
        scheduleId={scheduleId}
        employees={employees}
        tableZones={tableZones}
        prefillDate={createModalCell.date}
        prefillEmployeeId={createModalCell.employeeId}
        editShift={editShift}
        user={user}
        notifyOnPublish={modalNotify}
      />

      {/* ── Floating Add Shift button ── */}
      {subTab === 'grid' && activeCanEdit && schedule && (
        <button
          onClick={() => setAddMode(v => !v)}
          className={`fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3 rounded-full shadow-xl font-semibold text-sm transition-all duration-200 ${
            addMode
              ? 'bg-yellow-400 text-yellow-900 ring-4 ring-yellow-200 shadow-yellow-300'
              : 'bg-neutral-900 text-white hover:bg-neutral-700 hover:shadow-2xl'
          }`}
        >
          {addMode ? (
            <>
              <span className="text-base leading-none font-bold">✕</span>
              Cancel
            </>
          ) : (
            <>
              <span className="text-lg leading-none font-bold">+</span>
              Add Shift
            </>
          )}
        </button>
      )}
    </div>
  );
};

// ── Zone roster view ─────────────────────────────────────────────────────────

const ZoneRosterView = ({ weekDays, shifts, tableZones, activeCanEdit, restaurantId, scheduleId, onEditShift }) => {
  const [draggedShift, setDraggedShift] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null); // { zone: z | null, date }

  if (!tableZones.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-neutral-200 rounded-xl">
        <p className="text-sm text-neutral-500 font-medium">No floor zones configured</p>
        <p className="text-xs text-neutral-400 mt-1">Add zone numbers to your tables in the Tables settings to use this view.</p>
      </div>
    );
  }

  // Only Server shifts — this subtab is specifically for zone assignment of servers
  const assignedShifts = shifts.filter(s => !s.isOpenShift && s.employeeId && s.role === 'Server');

  const handleDrop = async (targetZone, targetDate) => {
    if (!draggedShift || !activeCanEdit) return;
    if (draggedShift.date !== targetDate) return; // can't move across days
    if (draggedShift.floorZone === targetZone) return; // no change
    try {
      await updateDoc(
        doc(db, 'restaurants_data', restaurantId, 'schedules', scheduleId, 'shifts', draggedShift.id),
        { floorZone: targetZone ?? null, updatedAt: serverTimestamp() }
      );
    } catch (err) {
      console.error('Zone assignment failed:', err);
    }
    setDraggedShift(null);
    setDragOverTarget(null);
  };

  const shiftCard = (s, baseCls) => (
    <button
      key={s.id}
      draggable={activeCanEdit}
      onDragStart={() => { if (activeCanEdit) setDraggedShift(s); }}
      onDragEnd={() => { setDraggedShift(null); setDragOverTarget(null); }}
      onClick={() => activeCanEdit && onEditShift(s)}
      disabled={!activeCanEdit}
      className={`w-full text-left px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${baseCls} ${
        activeCanEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
      } ${draggedShift?.id === s.id ? 'opacity-40' : ''}`}
    >
      <span className="block truncate">{s.employeeName}</span>
      <span className="block text-[10px] opacity-60 leading-tight">
        {formatTime(s.startTime)}–{formatTime(s.endTime)}
      </span>
    </button>
  );

  const dropProps = (zone, dateStr) => ({
    onDragOver: (e) => { e.preventDefault(); setDragOverTarget({ zone, date: dateStr }); },
    onDragLeave: () => setDragOverTarget(null),
    onDrop: (e) => { e.preventDefault(); handleDrop(zone, dateStr); },
  });

  const isOver = (zone, dateStr) =>
    dragOverTarget?.date === dateStr && dragOverTarget?.zone === zone &&
    draggedShift?.date === dateStr && draggedShift?.floorZone !== zone;

  return (
    <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-neutral-200 bg-neutral-50">
            <th className="text-left py-3 px-4 text-xs font-semibold text-neutral-500 uppercase tracking-wide w-28">Zone</th>
            {weekDays.map(day => {
              const dateStr = toDateStr(day);
              const isToday = dateStr === toDateStr(new Date());
              return (
                <th
                  key={dateStr}
                  className={`text-center py-3 px-2 text-xs font-semibold uppercase tracking-wide min-w-[110px] ${
                    isToday ? 'text-brand-blue' : 'text-neutral-500'
                  }`}
                >
                  {formatDayLabel(day)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {tableZones.map((z, idx) => (
            <tr key={z} className={`border-b border-neutral-100 ${idx % 2 === 1 ? 'bg-neutral-50/50' : ''}`}>
              <td className="py-3 px-4 align-top">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-blue/10 text-brand-blue text-xs font-semibold">
                  Zone {z}
                </span>
              </td>
              {weekDays.map(day => {
                const dateStr = toDateStr(day);
                const zShifts = assignedShifts.filter(s => s.date === dateStr && s.floorZone === z);
                const over = isOver(z, dateStr);
                return (
                  <td
                    key={dateStr}
                    className={`py-2 px-2 align-top transition-colors rounded ${over ? 'bg-brand-blue/10' : ''}`}
                    {...(activeCanEdit ? dropProps(z, dateStr) : {})}
                  >
                    <div className="space-y-1 min-h-[36px]">
                      {zShifts.length === 0 ? (
                        <div className={`text-xs text-center pt-2 ${over ? 'text-brand-blue' : 'text-neutral-300'}`}>
                          {over ? 'Drop here' : '—'}
                        </div>
                      ) : (
                        zShifts.map(s => shiftCard(s, 'bg-brand-blue/10 border-brand-blue/20 text-brand-blue hover:bg-brand-blue/20'))
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}

          {/* Unzoned row — Server shifts with no floor zone assigned */}
          <tr className="border-b border-neutral-100">
            <td className="py-3 px-4 align-top">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-500 text-xs font-semibold">
                No Zone
              </span>
            </td>
            {weekDays.map(day => {
              const dateStr = toDateStr(day);
              const unzoned = assignedShifts.filter(s => s.date === dateStr && s.floorZone == null);
              const over = isOver(null, dateStr);
              return (
                <td
                  key={dateStr}
                  className={`py-2 px-2 align-top transition-colors rounded ${over ? 'bg-neutral-100' : ''}`}
                  {...(activeCanEdit ? dropProps(null, dateStr) : {})}
                >
                  <div className="space-y-1 min-h-[36px]">
                    {unzoned.length === 0 ? (
                      <div className={`text-xs text-center pt-2 ${over ? 'text-neutral-500' : 'text-neutral-300'}`}>
                        {over ? 'Drop here' : '—'}
                      </div>
                    ) : (
                      unzoned.map(s => shiftCard(s, 'bg-neutral-100 border-neutral-200 text-neutral-600 hover:bg-neutral-200'))
                    )}
                  </div>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ── Changelog panel ──────────────────────────────────────────────────────────

const ACTION_LABELS = {
  shift_created:      'Created shift',
  shift_updated:      'Updated shift',
  shift_deleted:      'Deleted shift',
  schedule_published: 'Published schedule',
  week_duplicated:    'Duplicated previous week',
  day_cleared:        'Cleared day',
};

const ChangelogPanel = ({ changelog }) => {
  if (!changelog.length) {
    return (
      <div className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-neutral-200 rounded-xl">
        <p className="text-sm text-neutral-400">No activity yet for this week.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {changelog.map(entry => (
        <div key={entry.id} className="flex items-start gap-3 bg-white border border-neutral-200 rounded-xl px-4 py-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-neutral-700">
              {ACTION_LABELS[entry.action] || entry.action}
            </p>
            <p className="text-sm text-neutral-400 mt-0.5">
              by {entry.byName}
              {entry.at?.toDate
                ? ` · ${entry.at.toDate().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                : ''}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Coverage view helpers ────────────────────────────────────────────────────

const ROLE_HEX = {
  Server:     '#3b82f6',
  Host:       '#10b981',
  Cook:       '#f97316',
  Bartender:  '#8b5cf6',
  Dishwasher: '#78716c',
  Expo:       '#eab308',
  Manager:    '#ef4444',
  Owner:      '#f43f5e',
};
const ZONE_HEX = {
  FOH: '#0ea5e9',
  BOH: '#78716c',
  Bar: '#7c3aed',
};
const DEFAULT_BAR_HEX = '#9ca3af';

function barHex(shift, colorMode) {
  if (colorMode === 'zone') {
    if (shift.zone) return ZONE_HEX[shift.zone] || DEFAULT_BAR_HEX;
    return DEFAULT_BAR_HEX;
  }
  return ROLE_HEX[shift.role] || DEFAULT_BAR_HEX;
}

function tsToMins(ts) {
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.getHours() * 60 + d.getMinutes();
}

function timeStrToMins(str) {
  if (!str) return 0;
  const [h, m] = str.split(':').map(Number);
  return h * 60 + m;
}

function fmt12(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
}

/** Greedy lane assignment — shifts are sorted by start before calling this. */
function assignLanes(sorted) {
  const laneEnds = [];
  return sorted.map(shift => {
    const startM = tsToMins(shift.startTime);
    const endM   = tsToMins(shift.endTime);
    let lane = laneEnds.findIndex(end => end <= startM);
    if (lane === -1) { lane = laneEnds.length; }
    laneEnds[lane] = endM;
    return { ...shift, _lane: lane };
  });
}

// ── DayCoverageView ──────────────────────────────────────────────────────────

const DayCoverageView = ({ weekDays, shifts, employees, restaurantHours, colorMode, onEdit }) => {
  const empById = Object.fromEntries((employees || []).map(e => [e.id, e]));

  return (
    <div className="space-y-3">
      {weekDays.map((day) => {
        const dateStr = toDateStr(day);
        const isToday = dateStr === toDateStr(new Date());
        const dayShifts = shifts.filter(s => s.date === dateStr);

        // Find this day's restaurant hours (dayOfWeek: 0=Sun per JS getDay())
        const dow = day.getDay();
        const dayHours = (restaurantHours || []).find(h => h.dayOfWeek === dow);
        const restaurantClosed = dayHours?.isClosed || false;

        // Build the timeline window: start with restaurant hours, extend if shifts go outside
        let openM  = dayHours && !restaurantClosed ? timeStrToMins(dayHours.openTime)  : 11 * 60;
        let closeM = dayHours && !restaurantClosed ? timeStrToMins(dayHours.closeTime) : 22 * 60;

        if (dayShifts.length > 0) {
          openM  = Math.min(openM,  ...dayShifts.map(s => tsToMins(s.startTime)));
          closeM = Math.max(closeM, ...dayShifts.map(s => tsToMins(s.endTime)));
        }
        const daySpan = Math.max(closeM - openM, 1);

        // Tick marks every 2 hours
        const tickStart = Math.ceil(openM / 120) * 120;
        const ticks = [];
        for (let t = tickStart; t <= closeM; t += 120) ticks.push(t);

        // Split shifts: open (unassigned) vs assigned
        const openShifts     = dayShifts.filter(s => s.isOpenShift);
        const assignedShifts = dayShifts.filter(s => !s.isOpenShift);

        // Group by role or zone, sorted by start, lanes assigned
        const groupKey = (s) =>
          colorMode === 'zone' && s.zone ? s.zone : (s.role || 'Other');

        const buildGroupEntries = (list) => {
          const map = {};
          list.forEach(s => {
            const k = groupKey(s);
            if (!map[k]) map[k] = [];
            map[k].push(s);
          });
          return Object.entries(map)
            .map(([label, raw]) => {
              const sorted = [...raw].sort((a, b) => tsToMins(a.startTime) - tsToMins(b.startTime));
              return [label, assignLanes(sorted)];
            })
            .sort((a, b) => a[0].localeCompare(b[0]));
        };

        const openGroupEntries     = buildGroupEntries(openShifts);
        const assignedGroupEntries = buildGroupEntries(assignedShifts);

        return (
          <div
            key={dateStr}
            className={`bg-white rounded-xl border overflow-hidden ${isToday ? 'border-blue-300 ring-1 ring-blue-200' : 'border-neutral-200'}`}
          >
            {/* Day header */}
            <div className={`flex items-center justify-between px-4 py-2.5 border-b ${isToday ? 'bg-blue-50 border-blue-200' : 'bg-neutral-50 border-neutral-100'}`}>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-semibold ${isToday ? 'text-blue-700' : 'text-neutral-700'}`}>
                  {formatDayLabel(day)}
                </span>
                {dayHours && !restaurantClosed && (
                  <span className="text-sm text-neutral-400">
                    {fmt12(timeStrToMins(dayHours.openTime))} – {fmt12(timeStrToMins(dayHours.closeTime))}
                  </span>
                )}
                {restaurantClosed && (
                  <span className="text-sm font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                    Closed
                  </span>
                )}
                {!dayHours && (
                  <span className="text-sm text-neutral-300 italic">No hours on file</span>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-400">
                <span>{assignedShifts.length} assigned</span>
                {openShifts.length > 0 && (
                  <span className="text-amber-600 font-medium">{openShifts.length} open</span>
                )}
              </div>
            </div>

            {/* Timeline body */}
            <div className="px-4 py-3">
              {dayShifts.length === 0 ? (
                <div className="flex items-center justify-center h-8">
                  <span className="text-sm text-neutral-300 italic">No shifts scheduled</span>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {/* Time axis */}
                  <div className="relative h-4 ml-24">
                    {ticks.map(t => {
                      const pct = ((t - openM) / daySpan) * 100;
                      return (
                        <span
                          key={t}
                          className="absolute text-[10px] text-neutral-400 -translate-x-1/2 select-none"
                          style={{ left: `${pct}%` }}
                        >
                          {fmt12(t)}
                        </span>
                      );
                    })}
                    <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-200" />
                  </div>

                  {/* Open shifts — amber section, pinned to top */}
                  {openGroupEntries.length > 0 && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 -ml-3 px-3 py-2">
                      <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide">
                        Open {openShifts.length === 1 ? 'Shift' : 'Shifts'} ({openShifts.length})
                      </p>
                      {openGroupEntries.map(([groupLabel, groupShifts]) => {
                        const hex     = barHex(groupShifts[0], colorMode);
                        const maxLane = Math.max(...groupShifts.map(s => s._lane), 0);
                        return (
                          <div key={groupLabel} className="flex items-start gap-3">
                            <div className="w-20 shrink-0 pt-1 text-right">
                              <span className="text-sm font-semibold text-amber-700 leading-tight">{groupLabel}</span>
                            </div>
                            <div className="flex-1 space-y-1">
                              {Array.from({ length: maxLane + 1 }, (_, lane) => {
                                const laneShifts = groupShifts.filter(s => s._lane === lane);
                                return (
                                  <div key={lane} className="relative h-7 bg-amber-100 rounded border border-amber-200">
                                    {dayHours && !restaurantClosed && (
                                      <div
                                        className="absolute inset-y-0 rounded bg-amber-200 opacity-40"
                                        style={{
                                          left: `${Math.max(0, ((timeStrToMins(dayHours.openTime) - openM) / daySpan) * 100)}%`,
                                          width: `${Math.min(100, ((timeStrToMins(dayHours.closeTime) - timeStrToMins(dayHours.openTime)) / daySpan) * 100)}%`,
                                        }}
                                      />
                                    )}
                                    {laneShifts.map(shift => {
                                      const startM   = tsToMins(shift.startTime);
                                      const endM     = tsToMins(shift.endTime);
                                      const leftPct  = Math.max(0, ((startM - openM) / daySpan) * 100);
                                      const widthPct = Math.min(100 - leftPct, Math.max(0.5, ((endM - startM) / daySpan) * 100));
                                      return (
                                        <div
                                          key={shift.id}
                                          onClick={() => onEdit(shift)}
                                          title={`Open Shift · ${formatTime(shift.startTime)} – ${formatTime(shift.endTime)}${shift.notes ? ` · ${shift.notes}` : ''}`}
                                          className="absolute inset-y-0.5 rounded cursor-pointer flex flex-col justify-center px-1.5 hover:opacity-80 transition-opacity"
                                          style={{
                                            left:            `${leftPct}%`,
                                            width:           `${widthPct}%`,
                                            backgroundColor: `${hex}28`,
                                            border:          `1px solid ${hex}55`,
                                            borderLeftWidth: '3px',
                                            borderLeftColor: hex,
                                          }}
                                        >
                                          {widthPct > 7 && (
                                            <>
                                              <span className="text-[10px] font-semibold truncate leading-none" style={{ color: hex }}>Open</span>
                                              <span className="text-[10px] truncate leading-none opacity-75" style={{ color: hex }}>{fmt12(startM)}–{fmt12(endM)}</span>
                                            </>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Assigned shifts */}
                  {assignedGroupEntries.length > 0 && (
                    <div className="space-y-1.5">
                      {assignedGroupEntries.map(([groupLabel, groupShifts]) => {
                        const hex     = barHex(groupShifts[0], colorMode);
                        const maxLane = Math.max(...groupShifts.map(s => s._lane), 0);
                        return (
                          <div key={groupLabel} className="flex items-start gap-3">
                            <div className="w-20 shrink-0 pt-1 text-right">
                              <span className="text-sm font-semibold text-neutral-500 leading-tight">{groupLabel}</span>
                            </div>
                            <div className="flex-1 space-y-1">
                              {Array.from({ length: maxLane + 1 }, (_, lane) => {
                                const laneShifts = groupShifts.filter(s => s._lane === lane);
                                return (
                                  <div key={lane} className="relative h-7 bg-neutral-50 rounded border border-neutral-100">
                                    {dayHours && !restaurantClosed && (
                                      <div
                                        className="absolute inset-y-0 rounded bg-neutral-100 opacity-60"
                                        style={{
                                          left: `${Math.max(0, ((timeStrToMins(dayHours.openTime) - openM) / daySpan) * 100)}%`,
                                          width: `${Math.min(100, ((timeStrToMins(dayHours.closeTime) - timeStrToMins(dayHours.openTime)) / daySpan) * 100)}%`,
                                        }}
                                      />
                                    )}
                                    {laneShifts.map(shift => {
                                      const startM   = tsToMins(shift.startTime);
                                      const endM     = tsToMins(shift.endTime);
                                      const leftPct  = Math.max(0, ((startM - openM) / daySpan) * 100);
                                      const widthPct = Math.min(100 - leftPct, Math.max(0.5, ((endM - startM) / daySpan) * 100));
                                      const emp      = empById[shift.employeeId];
                                      const label    = emp ? emp.firstName : '';
                                      return (
                                        <div
                                          key={shift.id}
                                          onClick={() => onEdit(shift)}
                                          title={`${emp ? `${emp.firstName} ${emp.lastName}` : ''} · ${formatTime(shift.startTime)} – ${formatTime(shift.endTime)}${shift.notes ? ` · ${shift.notes}` : ''}`}
                                          className="absolute inset-y-0.5 rounded cursor-pointer flex flex-col justify-center px-1.5 hover:opacity-80 transition-opacity"
                                          style={{
                                            left:            `${leftPct}%`,
                                            width:           `${widthPct}%`,
                                            backgroundColor: `${hex}28`,
                                            border:          `1px solid ${hex}55`,
                                            borderLeftWidth: '3px',
                                            borderLeftColor: hex,
                                          }}
                                        >
                                          {widthPct > 7 && (
                                            <>
                                              <span className="text-[10px] font-semibold truncate leading-none" style={{ color: hex }}>{label}</span>
                                              <span className="text-[10px] truncate leading-none opacity-75" style={{ color: hex }}>{fmt12(startM)}–{fmt12(endM)}</span>
                                            </>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ScheduleView;
